import type { Access, AccessCheck } from '../../types';

/**
 * Resolves access for a single action purely from the server-computed access
 * rep's denied list, without the coarse built-in role heuristics that
 * `checkAccess` / `genericCheckAccess` apply first.
 *
 * Those heuristics short-circuit before the per-action denied list is consulted:
 * `genericCheckAccess('acct', ...)` treats anyone with `hasAdminRights` (which
 * counts *any* custom role or team custom role as admin) as allowed, and the
 * default allow-check `hasStrictWriterRights` treats any writer as allowed. For
 * privileged account-scoped actions such as the GenAI opt-ins — which the
 * backend denies to writers and to custom roles that don't explicitly grant
 * them — those heuristics can enable a control the backend then rejects with a
 * 403.
 *
 * This helper mirrors the backend decision exactly: allowed unless the action
 * appears in `access.denied`. When no access rep is present it fails open
 * (allowed); the backend remains the source of truth and enforces the real
 * permission.
 */
export function checkAccessFromDenials(access: Access | undefined, action: string): AccessCheck {
  const denied = access?.denied ?? [];
  const deniedAction = denied.find((v) => v.action === action);

  if (deniedAction === undefined) {
    return { isAllowed: true };
  }

  const reason = deniedAction.reason;

  return {
    isAllowed: false,
    appliedRoleName: reason?.role_name,
    reason,
  };
}
